import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'
import { getCachedAssignments, cacheAssignments, offlineSyncManager } from '@/Ship/Pwa/OfflineSync'
import { pwaManager } from '@/Ship/Pwa/PwaManager'

import type { AssignmentItem } from '../Components/AssignmentDetailDrawer'

class DriverDashboardManager {
    assignments: AssignmentItem[] = []
    loading = true
    cachedAt: number | null = null

    selectedAssignment: AssignmentItem | null = null
    drawerOpen = false

    // uuid of an assignment requested to open (e.g. from a notification) but not
    // yet present in the loaded list — opened once fetchAssignments resolves.
    pendingOpenUuid: string | null = null

    constructor() {
        makeAutoObservable(this)
        // Після завершення синхронізації офлайн-черги перезавантажуємо список,
        // щоб локальний state співпадав з фактичним станом сервера
        // (на випадок 400/conflict у деяких мутацій).
        offlineSyncManager.onSyncFinished(() => {
            if (navigator.onLine) {
                this.fetchAssignments().catch(() => {})
            }
        })
    }

    async fetchAssignments() {
        // Offline — serve cache only, nothing to revalidate.
        if (!navigator.onLine) {
            runInAction(() => {
                this.loading = true
            })
            const cached = await getCachedAssignments().catch(() => undefined)
            runInAction(() => {
                if (cached) {
                    this.setAssignments((cached.data as any)?.items ?? [])
                    this.cachedAt = cached.cachedAt
                } else {
                    this.setAssignments([])
                }
                this.loading = false
                this.resolvePendingOpen()
            })
            return
        }

        // Stale-while-revalidate: show whatever we already have (in-memory or
        // IndexedDB cache) immediately, then refresh from network in background.
        // Only show the spinner on a true cold start (no in-memory data).
        if (this.assignments.length === 0) {
            const cached = await getCachedAssignments().catch(() => undefined)
            runInAction(() => {
                if (cached && this.assignments.length === 0) {
                    this.setAssignments((cached.data as any)?.items ?? [])
                    this.cachedAt = cached.cachedAt
                    this.loading = false
                    this.resolvePendingOpen()
                } else {
                    this.loading = true
                }
            })
        } else {
            runInAction(() => {
                this.loading = false
            })
        }

        try {
            // Сервер вже приховує new (транспорт не розподілено) та завершені
            // завдання; дублюємо ключовий фільтр на клієнті як захист на випадок
            // legacy-даних з офлайн-кешу.
            const filters = JSON.stringify({
                and: [
                    { field: 'status', op: '!=', value: 'new' },
                    { field: 'status', op: '!=', value: 'completed_by_driver' },
                ],
                or: [],
            })
            const data = (await restClient.get(
                '/services/driver/assignments',
                { searchParams: { filters, limit: 300 } },
                { forceRefresh: true }
            )) as any
            const items: AssignmentItem[] = data?.items ?? []
            runInAction(() => {
                this.setAssignments(items)
                this.cachedAt = null
            })
            cacheAssignments(data).catch(() => {})
        } catch (err) {
            console.error('[DriverDashboardManager] fetch error', err)
            const cached = await getCachedAssignments().catch(() => undefined)
            if (cached) {
                const items: AssignmentItem[] = (cached.data as any)?.items ?? []
                runInAction(() => {
                    this.setAssignments(items)
                    this.cachedAt = cached.cachedAt
                })
            }
        } finally {
            runInAction(() => {
                this.loading = false
                this.resolvePendingOpen()
            })
        }
    }

    /** @param shift зміна задачі з відповіді сервера: дія водія прив'язує задачу до його зміни */
    updateAssignmentStatus(uuid: string, newStatus: string, shift?: AssignmentItem['shift']) {
        const COMPLETED_STATUSES = ['completed_by_driver', 'completed_by_dispatcher']

        if (COMPLETED_STATUSES.includes(newStatus)) {
            this.assignments = this.assignments.filter((a) => a.uuid !== uuid)
            if (this.selectedAssignment?.uuid === uuid) {
                this.selectedAssignment = null
                this.drawerOpen = false
            }
            return
        }

        const idx = this.assignments.findIndex((a) => a.uuid === uuid)
        if (idx !== -1) {
            this.assignments[idx] = { ...this.assignments[idx], status: newStatus, ...(shift ? { shift } : {}) }
        }
        if (this.selectedAssignment?.uuid === uuid) {
            this.selectedAssignment = { ...this.selectedAssignment, status: newStatus, ...(shift ? { shift } : {}) }
        }
    }

    /** @param confirmedAt момент підтвердження дії водієм (ISO) — фіксується в аудиті статусів */
    async changeStatus(uuid: string, newStatus: string, confirmedAt: string): Promise<void> {
        if (pwaManager.isOffline) {
            await offlineSyncManager.enqueue({
                type: 'status-change',
                uuid,
                newStatus,
                confirmedAt,
            })
            this.updateAssignmentStatus(uuid, newStatus)
            return
        }

        try {
            const updated = (await restClient.post(`/services/executor-assignments/${uuid}/change-status`, {
                json: { status: newStatus, confirmedAt },
            })) as unknown as Partial<AssignmentItem> | null
            this.updateAssignmentStatus(uuid, newStatus, updated?.shift)
        } catch (err: any) {
            console.error('[DriverDashboardManager] change-status error', err)
            await this.fetchAssignments()
            throw err
        }
    }

    openDrawer(assignment: AssignmentItem) {
        this.selectedAssignment = assignment
        this.drawerOpen = true
    }

    closeDrawer() {
        this.drawerOpen = false
    }

    // Open an assignment's drawer by uuid. If it is already in the list — open
    // immediately; otherwise remember it (so a background list refresh can
    // resolve it) and additionally fetch the single assignment by uuid. The
    // latter covers завершені завдання, які навмисно виключені зі списку
    // водія — без точкової вибірки картка зі сповіщення не відкрилась би.
    requestOpenByUuid(uuid: string) {
        const found = this.assignments.find((a) => a.uuid === uuid)
        if (found) {
            this.pendingOpenUuid = null
            this.openDrawer(found)
            return
        }
        this.pendingOpenUuid = uuid
        this.fetchAssignmentByUuid(uuid).catch(() => {})
    }

    // Fetch a single assignment by uuid (including completed ones, which the
    // default list hides). The collection endpoint returns them when filtered
    // explicitly by uuid. Opens the drawer if this uuid is still the pending one.
    private async fetchAssignmentByUuid(uuid: string): Promise<void> {
        if (!navigator.onLine) return
        try {
            const filters = JSON.stringify({
                and: [{ field: 'uuid', op: '=', value: uuid }],
                or: [],
            })
            const data = (await restClient.get(
                '/services/driver/assignments',
                { searchParams: { filters, limit: 1 } },
                { forceRefresh: true }
            )) as any
            const item: AssignmentItem | undefined = (data?.items ?? [])[0]
            if (item && this.pendingOpenUuid === uuid) {
                runInAction(() => {
                    this.pendingOpenUuid = null
                    this.openDrawer(item)
                })
            }
        } catch (err) {
            console.error('[DriverDashboardManager] fetchAssignmentByUuid error', err)
        }
    }

    // Assign the list with a deterministic order — freshest first — so the
    // "last assignment" on Головна matches the latest notification. We sort by
    // sortDate (updatedAt) desc, because распределение транспорта bumps it: a
    // just-distributed older task (smaller id) must surface to the top, which
    // sorting by id alone would not do. id is a stable tiebreaker, and the
    // fallback to id keeps старые кэшированные элементы без sortDate sane.
    private setAssignments(items: AssignmentItem[]) {
        this.assignments = [...items].sort((a, b) => {
            const ta = a.sortDate ? Date.parse(a.sortDate) : NaN
            const tb = b.sortDate ? Date.parse(b.sortDate) : NaN
            const aValid = !Number.isNaN(ta)
            const bValid = !Number.isNaN(tb)
            if (aValid && bValid && ta !== tb) return tb - ta
            // One side missing a valid date — push it below dated items.
            if (aValid !== bValid) return aValid ? -1 : 1
            return (b.id ?? 0) - (a.id ?? 0)
        })
    }

    // Resolve a pending open request after the assignments list changes.
    private resolvePendingOpen() {
        if (!this.pendingOpenUuid) return
        const found = this.assignments.find((a) => a.uuid === this.pendingOpenUuid)
        if (found) {
            this.pendingOpenUuid = null
            this.openDrawer(found)
        }
    }
}

export const driverDashboardManager = new DriverDashboardManager()
